class MediaGallerySlider {
  constructor(section) {
    this.section = section;
    this.viewport = section.querySelector('[data-slider-viewport]');
    this.slides = Array.from(section.querySelectorAll('[data-slider-slide]'));
    this.dots = Array.from(section.querySelectorAll('[data-slider-dot]'));
    this.videoTriggers = Array.from(section.querySelectorAll('[data-media-video-trigger]'));

    this.modal = section.querySelector('[data-media-modal]');
    this.modalBackdrop = section.querySelector('[data-media-modal-backdrop]');
    this.modalDialog = section.querySelector('[data-media-modal-dialog]');
    this.modalContent = section.querySelector('[data-media-modal-content]');
    this.modalClose = section.querySelector('[data-media-modal-close]');

    // Pagination is page-based (a page ≈ one viewport width), not one dot per
    // slide — otherwise the trailing dots point past the max scroll and go dead.
    this.pageCount = 1;
    this.currentPage = 0;
    this.maxScroll = 0;
    this.scrollFrame = null;
    this.lastTrigger = null;
    this.previousBodyOverflow = '';

    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);

    if (!this.viewport || this.slides.length === 0) return;

    this.measure();
    this.bindEvents();
    this.renderPagination();
  }

  bindEvents() {
    // Each visible dot represents a page; its index in the list is the page index.
    this.dots.forEach((dot, page) => {
      dot.addEventListener('click', () => this.scrollToPage(page));
    });

    this.videoTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.openVideoModal(trigger));
    });

    if (this.modalClose) {
      this.modalClose.addEventListener('click', () => this.closeVideoModal());
    }

    if (this.modalBackdrop) {
      this.modalBackdrop.addEventListener('click', (event) => {
        if (event.target === this.modalBackdrop) {
          this.closeVideoModal();
        }
      });
    }

    this.viewport.addEventListener(
      'scroll',
      () => {
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.setActiveFromScroll());
      },
      { passive: true }
    );

    this.viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollToPage(Math.min(this.currentPage + 1, this.pageCount - 1));
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.scrollToPage(Math.max(this.currentPage - 1, 0));
      }
    });

    // Page count depends on how many slides fit per view, which changes with the
    // viewport size and once lazy-loaded media settle — recompute on both.
    window.addEventListener('resize', () => this.refresh());
    window.addEventListener('load', () => this.refresh());

    if ('ResizeObserver' in window) {
      const track = this.slides[0] ? this.slides[0].parentElement : this.viewport;
      const observer = new ResizeObserver(() => this.refresh());
      observer.observe(this.viewport);
      if (track) observer.observe(track);
    }
  }

  openVideoModal(trigger) {
    if (!this.modal || !this.modalContent || !this.modalDialog) return;

    const shell = trigger.closest('[data-media-video]');
    if (!shell) return;

    this.lastTrigger = trigger;
    this.modalContent.replaceChildren();

    const template = shell.querySelector('[data-media-video-template]');

    if (template) {
      const content = template.content.cloneNode(true);
      this.modalContent.appendChild(content);

      const video = this.modalContent.querySelector('video');
      if (video) {
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
      }
    } else {
      const provider = shell.dataset.videoProvider;
      const videoId = shell.dataset.videoId;

      if (!provider || !videoId) return;

      const iframe = document.createElement('iframe');
      iframe.className = 'media-gallery-slider__video-element';
      iframe.title = trigger.dataset.mediaTitle || trigger.getAttribute('aria-label') || 'Video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.setAttribute('loading', 'eager');

      if (provider === 'youtube') {
        iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0&playsinline=1`;
      } else if (provider === 'vimeo') {
        iframe.src = `https://player.vimeo.com/video/${encodeURIComponent(videoId)}?autoplay=1`;
      } else {
        return;
      }

      this.modalContent.appendChild(iframe);
    }

    this.modalDialog.setAttribute(
      'aria-label',
      trigger.dataset.mediaTitle ? `Video: ${trigger.dataset.mediaTitle}` : 'Video player'
    );

    this.previousBodyOverflow = document.body.style.overflow;
    document.body.classList.add('media-gallery-modal-open');

    this.modal.hidden = false;

    requestAnimationFrame(() => {
      this.modal.classList.add('is-open');
      this.modalDialog.focus();

      const video = this.modalContent.querySelector('video');
      if (video) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      }
    });

    document.addEventListener('keydown', this.handleDocumentKeydown);
  }

  closeVideoModal() {
    if (!this.modal || this.modal.hidden) return;

    this.modal.classList.remove('is-open');
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    document.body.classList.remove('media-gallery-modal-open');
    document.body.style.overflow = this.previousBodyOverflow;

    const activeVideo = this.modalContent ? this.modalContent.querySelector('video') : null;
    if (activeVideo) activeVideo.pause();

    window.setTimeout(() => {
      if (this.modalContent) this.modalContent.replaceChildren();
      this.modal.hidden = true;

      if (this.lastTrigger) {
        this.lastTrigger.focus();
      }
    }, 180);
  }

  handleDocumentKeydown(event) {
    if (!this.modal || this.modal.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeVideoModal();
      return;
    }

    if (event.key !== 'Tab' || !this.modalDialog) return;

    const focusable = Array.from(
      this.modalDialog.querySelectorAll(
        'button:not([disabled]), a[href], iframe, video[controls], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden'));

    if (focusable.length === 0) {
      event.preventDefault();
      this.modalDialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // ---- Pagination (page-based) ----

  measure() {
    const pageWidth = this.viewport.clientWidth;
    this.maxScroll = Math.max(0, this.viewport.scrollWidth - pageWidth);
    // One page per viewport width, plus a final page for any remainder, so the
    // last dot always lands exactly at the end of the scroll range.
    this.pageCount = this.maxScroll <= 1 || pageWidth <= 0 ? 1 : Math.ceil(this.maxScroll / pageWidth) + 1;
    // Never show more pages than there are dots (fractional widths from peek/gaps
    // can otherwise inflate the count past the last real scroll position).
    const maxPages = this.dots.length > 0 ? this.dots.length : this.slides.length;
    this.pageCount = Math.min(this.pageCount, Math.max(1, maxPages));
    if (this.currentPage > this.pageCount - 1) this.currentPage = this.pageCount - 1;
  }

  pageStep() {
    return this.pageCount <= 1 ? 0 : this.maxScroll / (this.pageCount - 1);
  }

  refresh() {
    this.measure();
    this.renderPagination();
    this.setActiveFromScroll();
  }

  renderPagination() {
    this.dots.forEach((dot, index) => {
      const visible = index < this.pageCount;
      dot.hidden = !visible;
      if (visible) dot.setAttribute('aria-label', `Go to page ${index + 1} of ${this.pageCount}`);
    });
    this.updateActiveDot();
  }

  scrollToPage(page) {
    if (this.pageCount <= 1) return;
    const clamped = Math.max(0, Math.min(page, this.pageCount - 1));
    this.viewport.scrollTo({
      left: Math.round(this.pageStep() * clamped),
      behavior: 'smooth'
    });
    this.currentPage = clamped;
    this.updateActiveDot();
  }

  setActiveFromScroll() {
    let page = 0;
    if (this.pageCount > 1) {
      const step = this.pageStep();
      page = Math.max(0, Math.min(Math.round(this.viewport.scrollLeft / step), this.pageCount - 1));
    }

    if (page !== this.currentPage) {
      this.currentPage = page;
      this.updateActiveDot();
    }
  }

  updateActiveDot() {
    this.dots.forEach((dot, index) => {
      const isActive = index < this.pageCount && index === this.currentPage;
      dot.classList.toggle('is-active', isActive);

      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

const initializeMediaGallerySliders = (scope = document) => {
  scope.querySelectorAll('[data-media-gallery-slider]').forEach((section) => {
    if (section.dataset.mediaGalleryInitialized === 'true') return;
    section.dataset.mediaGalleryInitialized = 'true';
    new MediaGallerySlider(section);
  });
};

document.addEventListener('DOMContentLoaded', () => initializeMediaGallerySliders());

document.addEventListener('shopify:section:load', (event) => {
  initializeMediaGallerySliders(event.target);
});
